import { MenuItem } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import React, { MouseEventHandler, ReactNode } from 'react';
import Tooltip from './Tooltip';

interface Props {
    children: ReactNode;
    className?: string;
    onClick?: MouseEventHandler;
    disabled?: boolean;
    refObject?: React.MutableRefObject<HTMLDivElement>;
    dataTestId?: string;
    closeOnClick?: boolean;
    /** Explains an inert item — typically why it is disabled. */
    tooltipLabel?: string;
}

const MenuItemComponent = ({
    children,
    className = '',
    onClick,
    disabled = false,
    refObject,
    dataTestId,
    closeOnClick = false,
    tooltipLabel,
}: Props) => (
    <MenuItem>
        {({ focus, close }) => (
            // Headless UI renders MenuItem as a fragment and clones its own
            // props — role="menuitem", the ref, and the handler that closes the
            // menu — onto this one child. It must stay a DOM element: a
            // component here takes those props as its own and drops them, and
            // the menu then never closes and holds the rest of the page inert.
            // Hence the tooltip wraps the content, not the item.
            <div
                className={twMerge(
                    'flex flex-1 items-center rounded-sm p-2',
                    focus ? 'bg-gray-200' : '',
                    disabled
                        ? 'cursor-default text-gray-200 dark:text-gray-500'
                        : 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-400',
                    className,
                )}
                onClick={
                    disabled
                        ? null
                        : (event) => {
                              onClick?.(event);
                              if (closeOnClick) close();
                          }
                }
                ref={refObject}
                data-test-id={dataTestId}
            >
                <Tooltip
                    label={tooltipLabel}
                    tooltipClassName="flex flex-1 items-center"
                >
                    {children}
                </Tooltip>
            </div>
        )}
    </MenuItem>
);

export default MenuItemComponent;
